import { useCallback, useState } from 'react';
import { FavoriteQuery } from '../types/query';
import { queryService } from '../service/queryService';
import { useTranslation } from 'react-i18next';
import { showToast } from '../../../../lib/toast';

type NewFavoriteQuery = {
  Id: string;
  QueryName: string;
  QueryText: string;
};

export const useFavorites = ({
  setError,
}: {
  setError: (value: React.SetStateAction<string>) => void;
}) => {
  const { t } = useTranslation('sqlengine');
  const [favorites, setFavorites] = useState<FavoriteQuery[]>([]);
  const [loader, setLoader] = useState(true);

  const loadFavorites = useCallback(async () => {
    setLoader(true);
    try {
      const data = await queryService.getFavorites();
      setFavorites(data);
    } catch (error) {
      console.log(error);
      setError(t('sqlengine.error.favorite'));
    } finally {
      setLoader(false);
    }
  }, [setError, t]);

  const addFavorite = async (favorite: NewFavoriteQuery) => {
    const trimmedName = favorite.QueryName.trim();
    const trimmedQuery = favorite.QueryText.trim();

    if (!trimmedName || !trimmedQuery) {
      setError(t('sqlengine.error.saveQuery'));
      return;
    }

    const alreadyExists = favorites.some(
      (f) => f.QueryName.toLowerCase() === trimmedName.toLowerCase(),
    );

    if (alreadyExists) {
      setError(t('sqlengine.error.queryExists'));
      return;
    }

    try {
      const newFavorite = await queryService.saveFavoriteQuery({
        Id: favorite.Id,
        QueryName: trimmedName,
        QueryText: trimmedQuery,
      });
      setFavorites((prev) => [...prev, newFavorite]);
      showToast({
        type: 'success',
        title: t('sqlengine.success.querySaved'),
      });
      return;
    } catch (err) {
      console.error(err);
      setError(t('sqlengine.error.failedToAddFavorite'));
      return;
    }
  };

  const deleteFavoriteQuery = async (queryId: string) => {
    try {
      await queryService.deleteFavoriteQuery(queryId);
      loadFavorites();
      showToast({
        type: 'success',
        title: 'Query removed from favorites!',
      });
    } catch (err) {
      console.error('Error deleting favorite:', err);
      setError('Failed to delete the query.');
    }
  };

  return {
    favorites,
    addFavorite,
    reloadFavorites: loadFavorites,
    loader,
    deleteFavoriteQuery,
  };
};
